/**
 * PollDetailPage - View & interact with a single poll
 * Route: /trips/:id/polls/:pollId
 * Design: tripways.pen node pALSF/H2UpKB (desktop open/closed) / Q6NXp/zLGJD (mobile open/closed)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Clock4, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { apiGet } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { usePollDetail } from '../hooks/usePolls';
import { PollApiError } from '../services/pollService';
import { formatCountdown } from '../utils/dates';
import DesktopSidebar from '../components/common/DesktopSidebar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingScreen from '../components/common/LoadingScreen';
import PollOptionRow from '../components/polls/PollOptionRow';
import type { TripDetailResponse } from '../types/trip';
import type { MemberRole } from '../types/member';

export default function PollDetailPage() {
  const { id, pollId } = useParams<{ id: string; pollId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [myRole, setMyRole] = useState<MemberRole>('EDITOR');
  const { poll, loading, vote, revokeVote, closePoll } = usePollDetail(id, pollId);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiGet<TripDetailResponse>(`/trip/${id}`).then((response) => {
      if (response.exitCode === 1 && response.data) setMyRole(response.data.myRole);
    });
  }, [id]);

  if (!id || !pollId || !user) return null;
  if (loading || !poll) {
    return <LoadingScreen />;
  }

  const isOpen = poll.status === 'OPEN';
  const totalVotes = poll.options.reduce((sum, o) => sum + o.voteCount, 0);
  const maxVoteCount = Math.max(0, ...poll.options.map((o) => o.voteCount));
  const canClose = myRole === 'OWNER' || poll.createUserId === user.userId;
  const countdown = isOpen && poll.expiresAt ? formatCountdown(new Date(poll.expiresAt)) : null;
  const selectedCount = poll.options.filter((o) => o.myVoted).length;
  const atMultiLimit = poll.voteType === 'MULTI' && poll.maxVotes != null && selectedCount >= poll.maxVotes;

  const handleToggle = async (optionId: string, myVoted: boolean) => {
    try {
      if (myVoted) await revokeVote(optionId);
      else await vote(optionId);
    } catch (err) {
      showToast('error', err instanceof PollApiError ? err.message : '操作失敗');
    }
  };

  const handleClose = async () => {
    try {
      await closePoll();
      showToast('success', '已結束投票');
    } catch (err) {
      showToast('error', err instanceof PollApiError ? err.message : '結束投票失敗');
    } finally {
      setConfirmClose(false);
    }
  };

  const statusTag = isOpen ? (
    <span className="text-[11px] font-semibold text-primary bg-[#e0f9fe] rounded-lg px-2.5 py-1">進行中</span>
  ) : (
    <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-lg px-2.5 py-1">已結束</span>
  );

  const metaRow = (
    <div className="flex items-center gap-4 text-[13px] text-slate-500 flex-wrap">
      <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-lg px-2.5 py-1">
        {poll.voteType === 'SINGLE' ? '單選' : '多選'}
      </span>
      <span>{totalVotes} 票</span>
      <span>由 {poll.creatorNickname} 發起</span>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f5f8f8]">
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="flex items-center justify-center w-11 h-11 -ml-2">
                <ChevronLeft size={24} className="text-slate-900" />
              </button>
              <h1 className="text-slate-900 text-[17px] font-bold">投票詳情</h1>
            </div>
            {statusTag}
          </div>
        </header>

        {/* ─── Desktop Top Bar ─── */}
        <header className="hidden lg:flex items-center justify-between bg-white border-b border-slate-100 px-10 py-5">
          <button
            onClick={() => navigate(`/trips/${id}/polls`)}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            返回投票列表
          </button>
          <div className="flex items-center gap-2.5">
            {statusTag}
            {countdown && (
              <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-500 bg-orange-50 rounded-lg px-2.5 py-1">
                <Clock4 size={12} />
                {countdown}
              </span>
            )}
          </div>
        </header>

        {/* ─── Main ─── */}
        <main className="flex-1 px-4 lg:px-10 py-5 lg:py-8">
          <div className="max-w-3xl mx-auto flex flex-col gap-5">
            <div className="rounded-xl bg-white shadow-sm p-5 lg:p-6 flex flex-col gap-2">
              <h1 className="text-slate-900 text-lg lg:text-[22px] font-bold">{poll.subject}</h1>
              {metaRow}
              {!isOpen && poll.closedAt && (
                <p className="text-xs text-slate-400">已於 {format(new Date(poll.closedAt), 'yyyy/M/d HH:mm')} 結束</p>
              )}
            </div>

            <div className="flex flex-col gap-2.5">
              {poll.options.map((option) => (
                <PollOptionRow
                  key={option.id}
                  option={option}
                  totalVotes={totalVotes}
                  status={poll.status}
                  isWinner={!isOpen && maxVoteCount > 0 && option.voteCount === maxVoteCount}
                  disabled={isOpen && !option.myVoted && atMultiLimit}
                  onToggle={isOpen ? () => handleToggle(option.id, option.myVoted) : undefined}
                />
              ))}
            </div>

            {isOpen && (
              <p className="text-center text-xs text-slate-400">
                {poll.voteType === 'SINGLE' ? '點擊選項即可投票，再次點擊可收回' : '可複選，點擊選項即可投票或收回'}
              </p>
            )}

            {isOpen && canClose && (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <button
                  onClick={() => setConfirmClose(true)}
                  className="flex items-center gap-1.5 rounded-[10px] border border-red-500 text-red-500 text-sm font-medium px-5 py-2.5"
                >
                  <Lock size={14} />
                  手動關閉投票
                </button>
                <span className="text-xs text-slate-400">僅投票發起人或旅程擁有者可見</span>
              </div>
            )}
          </div>
        </main>
      </div>

      <ConfirmDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={handleClose}
        title="結束投票"
        message="確定要手動結束這個投票嗎？結束後所有成員將無法再投票或收回票。"
        confirmText="結束投票"
      />
    </div>
  );
}
